import { stockLevel } from '@pl/domain';
import type { OrderDto, SiteDto, SkuDto, StreamEventDto } from '@pl/domain';
import { Fragment, useMemo } from 'react';
import { Link } from 'react-router';
import { describeEvent, eventDot } from '../../lib/describe';
import { CHANNEL_DOT, channelName, clockTime, skuName } from '../../lib/format';
import { useOrders, useSiteMap, useSites, useSkuMap, useSkus, useStock } from '../../lib/queries';
import { useLiveFeed, useLiveSite } from '../../lib/useLive';
import { Bar, Chip, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner, Swatch, shortName } from '../../ui/bits';
import { Icon, type IconName } from '../../ui/Icon';
import { LiveDot } from '../../ui/LiveDot';
import { useSiteScope } from '../useSiteScope';
import s from './Dashboard.module.css';

function dueLabel(requiredBy: string): string {
  return new Date(`${requiredBy}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function scanTone(order: OrderDto): 'ok' | 'accent' | undefined {
  if (order.status === 'picking' && order.scanned === order.totalQty) return 'ok';
  if (order.scanned > 0) return 'accent';
  return undefined;
}

const DOT = { accent: s.feedDotAccent, bad: s.feedDotBad, ok: s.feedDotOk, ink: s.feedDotInk };

/** One line of Live activity: the time at the event's site, a dot for what kind of thing it was, and what happened. */
function FeedRow({ event, skuMap, siteMap }: { event: StreamEventDto; skuMap: Map<string, SkuDto>; siteMap: Map<string, SiteDto> }) {
  const timeZone = event.siteId ? siteMap.get(event.siteId)?.timezone : undefined;
  return (
    <li>
      <time dateTime={event.at}>{clockTime(event.at, timeZone)}</time>
      <i className={DOT[eventDot(event)]} aria-hidden="true" />
      <span className={s.feedLine}>
        {describeEvent(event, skuMap, siteMap).map((part, i) =>
          part.kind === 'who' ? (
            <b key={i}>{part.text}</b>
          ) : part.kind === 'sku' ? (
            <SkuTag key={i} sku={part.sku} short />
          ) : part.kind === 'ref' ? (
            <span key={i} className="mono">
              {part.text}
            </span>
          ) : (
            <Fragment key={i}>{part.text}</Fragment>
          ),
        )}
      </span>
    </li>
  );
}

export function Dashboard() {
  const sitesQuery = useSites();
  const stockQuery = useStock();
  const ordersQuery = useOrders({ status: ['open', 'picking'], limit: 200 });
  const skusQuery = useSkus({ includeInactive: true });
  const siteMap = useSiteMap();
  const skuMap = useSkuMap();
  const sites = sitesQuery.data ?? [];
  const stock = stockQuery.data ?? [];
  const orders = ordersQuery.data ?? [];
  const { siteId } = useSiteScope();
  const feed = useLiveFeed(12);
  const liveSite = useLiveSite();
  const feedScope = liveSite === 'all' ? 'all sites' : liveSite ? (siteMap.get(liveSite)?.shortCode ?? '') : '';

  const siteTotals = useMemo(() => {
    const map = new Map<string, { available: number; inStock: number; reserved: number }>();
    for (const row of stock) {
      const cur = map.get(row.siteId) ?? { available: 0, inStock: 0, reserved: 0 };
      cur.available += row.available;
      cur.inStock += row.inStock;
      cur.reserved += row.reserved;
      map.set(row.siteId, cur);
    }
    return map;
  }, [stock]);

  const ordersBySite = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of orders) map.set(o.siteId, (map.get(o.siteId) ?? 0) + 1);
    return map;
  }, [orders]);

  const shipSite = siteId ? siteMap.get(siteId) : sites.find((x) => x.live);
  const shipOrders = useMemo(() => orders.filter((o) => o.siteId === shipSite?.id), [orders, shipSite]);

  const attentionItems = useMemo(() => {
    const items: { key: string; tone: 'bad' | 'warn'; text: string; icon?: IconName }[] = [];
    for (const row of stock) {
      if (stockLevel(row.available) !== 'short') continue;
      const site = siteMap.get(row.siteId);
      const sku = skuMap.get(row.skuId);
      items.push({ key: `stock-${row.siteId}-${row.skuId}`, tone: 'bad', text: `${site?.shortCode ?? row.siteId} · ${skuName(sku)} at ${row.available} available` });
    }
    // Out and low, per live site, by the rule Office → Stock and the Wall use (`stockLevel`): every
    // active SKU, a SKU with no stock there counting as none. One line a site, not one a SKU.
    const bySiteSku = new Map(stock.map((r) => [`${r.siteId}|${r.skuId}`, r.available]));
    const activeSkus = [...skuMap.values()].filter((k) => k.active);
    for (const site of sites.filter((x) => x.live)) {
      let out = 0;
      let low = 0;
      for (const sku of activeSkus) {
        const level = stockLevel(bySiteSku.get(`${site.id}|${sku.id}`) ?? 0);
        if (level === 'out') out++;
        else if (level === 'low') low++;
      }
      if (out === 0 && low === 0) continue;
      const parts = [out > 0 && `${out} ${out === 1 ? 'SKU' : 'SKUs'} out`, low > 0 && `${low} low`].filter(Boolean);
      items.push({ key: `level-${site.id}`, tone: 'warn', icon: 'stock', text: `${site.shortCode} · ${parts.join(', ')}` });
    }
    for (const o of orders) {
      const site = siteMap.get(o.siteId);
      if (!o.requiredBy || !site) continue;
      const today = new Date().toLocaleDateString('en-CA', { timeZone: site.timezone });
      if (o.requiredBy >= today) continue;
      items.push({ key: `order-${o.id}`, tone: 'warn', text: `${o.externalRef ?? o.code} was due ${dueLabel(o.requiredBy)}` });
    }
    return items;
  }, [stock, orders, siteMap, skuMap, sites]);
  const attnTone = attentionItems.some((i) => i.tone === 'bad') ? 'bad' : attentionItems.some((i) => i.tone === 'warn') ? 'warn' : 'ok';

  const liveSites = useMemo(() => sites.filter((x) => x.live), [sites]);
  const lowest = useMemo(
    () =>
      [...skuMap.values()]
        .filter((sku) => sku.active)
        .map((sku) => {
          const perSite = liveSites.map((site) => {
            const row = stock.find((r) => r.siteId === site.id && r.skuId === sku.id);
            return { site, available: row?.available ?? 0, inStock: row?.inStock ?? 0 };
          });
          const total = perSite.reduce((sum, p) => sum + p.available, 0);
          return { sku, perSite, total };
        })
        .sort((a, b) => a.total - b.total)
        .slice(0, 6),
    [skuMap, liveSites, stock],
  );

  // The page-wide error is for a query with nothing to show. One that fails a background refresh
  // keeps its data — stock refreshes every 20 seconds — and says so in a RefetchNote instead.
  const queries = [sitesQuery, stockQuery, ordersQuery, skusQuery];
  const failedQueries = queries.filter((q) => q.isLoadingError);
  if (failedQueries.length > 0) {
    return (
      <div className="panel">
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={failedQueries[0]!.error} />
          <button type="button" className="btn" onClick={() => failedQueries.forEach((q) => q.refetch())}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!sitesQuery.data || !stockQuery.data || !ordersQuery.data || !skusQuery.data) {
    return (
      <div className="row">
        <Spinner label="Loading dashboard" />
      </div>
    );
  }

  return (
    <>
      <RefetchNote queries={queries} />
      <div className={s.sites}>
        {sites.map((site) => {
          const totals = siteTotals.get(site.id) ?? { available: 0, inStock: 0, reserved: 0 };
          const toShip = ordersBySite.get(site.id) ?? 0;
          return (
            <div key={site.id} className={site.live ? s.site : `${s.site} ${s.off}`} data-testid={`site-${site.id}`}>
              <div className={s.siteH}>
                <SiteTag site={site} />
                <b>{site.name}</b>
                <span className="grow" />
                {site.live ? (
                  <Chip tone="ok" dot>
                    Live
                  </Chip>
                ) : (
                  <Chip>Not live</Chip>
                )}
              </div>
              {site.live ? (
                <>
                  <div className={s.avail}>
                    <span className="metric-v num">{totals.available}</span>
                    <span className="metric-s">boxes available</span>
                  </div>
                  <div className={s.splits}>
                    <div>
                      <b>{totals.inStock}</b>in stock
                    </div>
                    <div>
                      <b>{totals.reserved}</b>reserved
                    </div>
                    <div>
                      <b>{toShip}</b>to ship today
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={s.avail}>
                    <span className="metric-v num">—</span>
                    <span className="metric-s">Stocktake pending</span>
                  </div>
                  <div className={s.splits}>
                    <div>
                      <b>{totals.inStock}</b>in stock
                    </div>
                    <div>
                      <b>{totals.reserved}</b>reserved
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className={s.grid2}>
        <div className="panel">
          <div className="panel-h">
            <h2>Ship today</h2>
            <SiteTag site={shipSite} />
            <span className="grow" />
            <Link to="/office/orders" className="link arrow" style={{ fontSize: '12.5px' }}>
              All orders
            </Link>
          </div>
          <table className="t" aria-label="Ship today">
            <thead>
              <tr>
                <th>Order</th>
                <th>Channel</th>
                <th>Customer</th>
                <th>Lines</th>
                <th>Scanned</th>
              </tr>
            </thead>
            <tbody>
              {shipOrders.map((o) => (
                <tr key={o.id}>
                  <td className="mono">
                    <Link to={`/office/orders/${o.id}`}>{o.externalRef ?? o.code}</Link>
                  </td>
                  <td>
                    <span className="channel">
                      <i style={{ background: CHANNEL_DOT[o.channel] }} />
                      {channelName(o.channel)}
                    </span>
                  </td>
                  <td>{o.customerName}</td>
                  <td>
                    <div className="lines">
                      {o.lines.map((l) => {
                        const sku = skuMap.get(l.skuId);
                        return (
                          <Chip key={l.id}>
                            <Swatch hex={sku?.colourHex ?? null} name={sku?.colourName ?? null} />
                            {shortName(sku?.name ?? 'Blank sticker')} ×{l.qty}
                          </Chip>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <div className={s.prog}>
                      <Bar value={o.scanned} max={o.totalQty} tone={scanTone(o)} />
                      <span>
                        {o.scanned}/{o.totalQty}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {shipOrders.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Nothing to ship today.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <div className="panel-h">
            <h2>Needs attention</h2>
            <Chip tone={attnTone}>{attentionItems.length}</Chip>
            <span className="grow" />
          </div>
          <ul aria-label="Needs attention" className={s.att}>
            {attentionItems.length === 0 ? (
              <li>
                <span className={`${s.ic} ${s.ok}`}>
                  <Icon name="check" />
                </span>
                <div>
                  <b>Nothing needs attention</b>
                </div>
              </li>
            ) : (
              attentionItems.map((item) => (
                <li key={item.key}>
                  <span className={`${s.ic} ${s[item.tone]}`}>
                    <Icon name={item.icon ?? (item.tone === 'bad' ? 'alert' : 'clock')} />
                  </span>
                  <div>
                    <b>{item.text}</b>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <div className={s.grid2}>
        <div className="panel">
          <div className="panel-h">
            <h2>Lowest availability</h2>
            <span className="grow" />
            <span className="muted" style={{ fontSize: '12px' }}>
              available · in stock
            </span>
          </div>
          <table className="t">
            <thead>
              <tr>
                <th>SKU</th>
                {liveSites.map((site) => (
                  <th key={site.id} className="r">
                    {site.shortCode}
                  </th>
                ))}
                <th className="r">Total</th>
              </tr>
            </thead>
            <tbody>
              {lowest.map(({ sku, perSite, total }) => (
                <tr key={sku.id}>
                  <td>
                    <SkuTag sku={sku} withCode />
                  </td>
                  {perSite.map((p) => (
                    <td key={p.site.id} className="r num" style={p.available < 0 ? { color: 'var(--bad)', fontWeight: 600 } : undefined}>
                      {p.available} <span className="faint">· {p.inStock}</span>
                    </td>
                  ))}
                  <td className="r num">{total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <div className="panel-h">
            <h2>Live activity</h2>
            <span className="grow" />
            <LiveDot label={feedScope} />
          </div>
          <ul className={s.feed} aria-label="Live activity">
            {feed.length === 0 ? (
              <li className={s.feedEmpty}>Nothing yet. Scans show up here as they happen.</li>
            ) : (
              feed.map((event) => <FeedRow key={event.id} event={event} skuMap={skuMap} siteMap={siteMap} />)
            )}
          </ul>
        </div>
      </div>
    </>
  );
}
